// src/components/AssistantChat.tsx
import React, { useState, useEffect, useRef } from 'react';
import { FiRefreshCw } from 'react-icons/fi';
import { chatInit, chatMessage, chatRestart, chatHistory } from '../../../api/chatService';
import damelioProfileImage from '../../../assets/damelio_chat2.jpg';
import { useTranslation } from 'react-i18next';

interface AssistantChatProps {
  reportId: string;
}

interface ChatItem {
  sender: string;
  text: string;
  time: Date;
}

const AssistantChat: React.FC<AssistantChatProps> = ({ reportId }) => {
  const { t } = useTranslation();

  const suggestionMessages = [
    t('suggest.explain_report'),
    t('suggest.subdomain'),
    t('suggest.typosquatting'),
    t('suggest.protect'),
  ];

  const [isOpen, setIsOpen] = useState(true);
  const [isMinimized, setIsMinimized] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatItem[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const initializedRef = useRef(false);

  // scroll automático
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const formatMessages = (data: any): ChatItem[] => {
    const msgs = Array.isArray(data)
      ? data
      : data?.messages && Array.isArray(data.messages)
      ? data.messages
      : [];
    return msgs.map((msg: any) => ({
      sender: msg.sender,
      text: msg.content,
      time: new Date(msg.timestamp),
    }));
  };

  // cargar historial
  useEffect(() => {
    const initChat = async () => {
      if (initializedRef.current) return;
      initializedRef.current = true;
      if (sessionId) return;

      try {
        const initData = await chatInit(reportId);
        setSessionId(initData.session_id);

        const historyResponse = await chatHistory(initData.session_id);
        setMessages(formatMessages(historyResponse));
        setHistoryLoaded(true);
      } catch (err) {
        console.error('Error al inicializar el chat:', err);
      }
    };

    if (isOpen) initChat();
  }, [isOpen, reportId, sessionId]);

  const handleSendMessage = async (message: string) => {
    if (!sessionId || !message.trim()) return;
    setMessages(prev => [...prev, { sender: 'user', text: message, time: new Date() }]);
    setIsTyping(true);
    setIsLoading(true);

    try {
      const response = await chatMessage(sessionId, message);
      setMessages(prev => [
        ...prev,
        { sender: 'damelio', text: response.response, time: new Date() },
      ]);
    } catch (err) {
      console.error('Error enviando mensaje:', err);
    } finally {
      setIsTyping(false);
      setIsLoading(false);
    }
  };

  const handleRestartChat = async () => {
    if (!sessionId) return;
    setHistoryLoaded(false);
    try {
      await chatRestart(sessionId);
      const initData = await chatInit(reportId);
      initializedRef.current = false;
      setSessionId(initData.session_id);

      const historyResponse = await chatHistory(initData.session_id);
      setMessages(formatMessages(historyResponse));
      setHistoryLoaded(true);
    } catch (err) {
      console.error('Error reiniciando chat:', err);
    } finally {
      setShowModal(false);
    }
  };

  const hasUserMessage = messages.some(m => m.sender === 'user');

  return (
    <div className="fixed bottom-4 right-4 z-50" style={{ width: isMinimized ? '350px' : '450px' }}>
      {isOpen ? (
        <div className="bg-white rounded-lg shadow-xl border border-gray-300 flex flex-col h-full">
          {/* Header */}
          <div className="bg-gray-800 text-white p-4 rounded-t-lg flex justify-between items-center">
            <span
              className="font-bold text-lg flex-1 text-center cursor-pointer"
              onClick={() => setIsMinimized(!isMinimized)}
            >
              {t('chat_title')}
            </span>
            {sessionId && (
              <button
                onClick={() => setShowModal(true)}
                className="ml-2 focus:outline-none"
                title={t('restart_chat')}
              >
                <FiRefreshCw />
              </button>
            )}
            <button
              onClick={e => {
                e.stopPropagation();
                setIsOpen(false);
                setIsMinimized(false);
              }}
              className="ml-2 focus:outline-none"
            >
              ✖
            </button>
          </div>

          {!isMinimized && (
            <>
              {/* Mensajes / Loader */}
              <div className="p-4 flex-1 overflow-y-auto" style={{ maxHeight: '600px' }}>
                {!historyLoaded ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin rounded-full h-8 w-8 border-4 border-gray-200 border-t-blue-500" />
                  </div>
                ) : (
                  messages.map((msg, idx) => (
                    <React.Fragment key={idx}>
                      {/* Burbuja */}
                      <div className="text-center text-xs text-gray-500 mb-2">
                        {msg.time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                      <div className={`mb-4 flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                        {msg.sender === 'damelio' && (
                          <img
                            src={damelioProfileImage}
                            alt="Bot"
                            className="w-10 h-10 rounded-full mr-3 self-start"
                          />
                        )}
                        <div
                          className={`px-4 py-2 rounded-lg ${
                            msg.sender === 'user'
                              ? 'bg-blue-500 text-white'
                              : 'bg-gray-200 text-gray-800'
                          }`}
                          style={{ maxWidth: '80%' }}
                        >
                          {msg.text}
                        </div>
                      </div>

                      {/* Sugerencias justo tras el primer mensaje del bot */}
                      {idx === 0 && !hasUserMessage && !isTyping && !isLoading && (
                        <div className="p-2 flex flex-wrap justify-center space-x-2 space-y-2">
                          {suggestionMessages.map((sug, i) => (
                            <button
                              key={i}
                              onClick={() => handleSendMessage(sug)}
                              className="px-4 py-2 rounded-full bg-gray-200 text-gray-800 hover:bg-gray-300 transition"
                            >
                              {sug}
                            </button>
                          ))}
                        </div>
                      )}
                    </React.Fragment>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="p-4 border-t">
                <form
                  onSubmit={e => {
                    e.preventDefault();
                    const frm = e.target as HTMLFormElement;
                    const inp = frm.elements.namedItem('message') as HTMLInputElement;
                    handleSendMessage(inp.value);
                    inp.value = '';
                  }}
                  className="flex items-center"
                >
                  <input
                    name="message"
                    type="text"
                    placeholder={t('message_placeholder')}
                    className="flex-1 p-2 border rounded-l-lg"
                    disabled={isTyping || isLoading}
                    autoComplete="off"
                  />
                  <button
                    type="submit"
                    className="bg-blue-500 text-white px-4 py-2 rounded-r-lg hover:bg-blue-600"
                    disabled={isTyping || isLoading}
                  >
                    {t('send')}
                  </button>
                </form>
              </div>
            </>
          )}

          {/* Modal de reinicio */}
          {showModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-lg p-6 w-96">
                <h2 className="text-lg font-bold mb-4">{t('restart_chat')}</h2>
                <p className="mb-4">{t('restart_chat_confirmation')}</p>
                <div className="flex justify-end">
                  <button
                    onClick={() => setShowModal(false)}
                    className="mr-4 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                  >
                    {t('cancel')}
                  </button>
                  <button
                    onClick={handleRestartChat}
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    {t('restart')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <button
          onClick={() => setIsOpen(true)}
          className="bg-gray-800 text-white p-4 rounded-full shadow-lg focus:outline-none"
          style={{ position: 'fixed', bottom: '16px', right: '16px' }}
        >
          💬
        </button>
      )}
    </div>
  );
};

export default AssistantChat;
